import { getOpportunityList, getOpportunityExpiredList, getOpportunityDetails, buyOpportunity, discardOpportunity, getSpreadsheetOpportunities } from 'Api/quoteOpportunityApi'
import { parseOpportunityPreview, parseOpportunityDetail, parsePublicOpportunityDetail, parseExpiredQuotation } from 'Parsers/opportunityParser'
import iconGrid3 from 'icon/win10/grid-3.svg?component'
import iconGrid2 from 'icon/win10/grid-2.svg?component'
import { getPlanStatus } from 'Api/subscriptionApi'
import opportunitySortType from 'Enums/opportunitySortType'
import QuoteOpportunityState from 'Types/_Store/QuoteOpportunity'

const state: QuoteOpportunityState = {
  requesting: {
    companyDiscouny: false,
    opportunityList: false,
    purchase: false,
    details: false,
  },

  pagination: {
    current: 1,
    limit: 1,
  },

  selected: {
    mode: 0,
    opportunity: null,
    sort: opportunitySortType.expiresSoon,
    discardReasonId: 0

  },

  modeList: [
    { id: 0, type: 'list', name: 'Em lista', icon: iconGrid3 },
    { id: 1, type: 'grid', name: 'Em grade', icon: iconGrid2 },
  ],

  companyDiscount: 0,
  opportunityList: [],
  expiredList: [],
}

const mutations = {
  request_start(state, type) {
    state.requesting[type] = true
  },

  request_success(state, type) {
    state.requesting[type] = false
  },

  request_failure(state, type) {
    state.requesting[type] = false
  },

  set_opportunity_list(state, opportunityList) {
    state.opportunityList = opportunityList
  },

  set_expired_list(state, expiredList) {
    state.expiredList = expiredList
  },

  set_company_discount(state, companyDiscount) {
    state.companyDiscount = companyDiscount
  },

  set_pagination(state, pagination) {
    Object.assign(state.pagination, pagination)
  },

  set_selected(state, selected) {
    Object.assign(state.selected, selected)
  }
}

const actions = {
  async getCompanyDiscount({ commit }) {
    commit('request_start', 'companyDiscount')

    try {
      const { data } = await getPlanStatus()
      commit('request_success', 'companyDiscount')
      commit('set_company_discount', data.benefit.percentageDiscountQuotations)

      return true
    } catch (err) {
      commit('request_failure', 'companyDiscount')

      throw err
    }
  },

  async getOpportunityListOrder({ state, commit }) {
    const { pagination, selected } = state
    commit('set_opportunity_list', [])
    commit('set_pagination', { current: 1 })

    try {
      commit('request_start', 'opportunityList')

      const [opportunityResult, expiredResult] = await Promise.all([
        getOpportunityList(pagination.current, { sort: selected.sort }),
      ])

      const opportunityList = opportunityResult.data.items.map(parseOpportunityPreview)
      const limit = opportunityResult.data.pageCount

      commit('set_pagination', { current: 1, limit: limit })

      commit('set_opportunity_list', opportunityList)

      commit('request_success', 'opportunityList')

      return opportunityList
    } catch (err) {
      commit('request_failure', 'opportunityList')

      throw err
    }

  },

  async getOpportunityList({ state, commit }) {
    const { pagination, selected } = state
    const { opportunityList: currentOpportunityList } = state

    try {
      commit('request_start', 'opportunityList')

      const [opportunityResult, expiredResult] = await Promise.all([
        getOpportunityList(pagination.current, { sort: selected.sort }),
        getOpportunityExpiredList(),
      ])

      const expiredList = expiredResult.data.map(parseExpiredQuotation)
      const opportunityList = opportunityResult.data.items.map(parseOpportunityPreview)
      const limit = opportunityResult.data.pageCount

      commit('set_pagination', { limit })

      commit('set_expired_list', expiredList)

      commit('set_opportunity_list', [
        ...currentOpportunityList,
        ...opportunityList
      ])

      commit('request_success', 'opportunityList')

      return opportunityList
    } catch (err) {
      commit('request_failure', 'opportunityList')

      throw err
    }
  },

  async reloadOpportunityList({ state, commit }) {
    try {
      commit('set_opportunity_list', [])
      commit('request_start', 'opportunityList')

      const [opportunityResult, expiredResult] = await Promise.all([
        getOpportunityList(1),
        getOpportunityExpiredList(),
      ])

      const expiredList = expiredResult.data.map(parseExpiredQuotation)
      const opportunityList = opportunityResult.data.items.map(parseOpportunityPreview)
      const limit = opportunityResult.data.pageCount

      commit('set_pagination', { current: 1, limit: limit })
      commit('set_expired_list', expiredList)
      commit('set_opportunity_list', opportunityList)

      commit('request_success', 'opportunityList')

      return opportunityList
    } catch (err) {
      commit('request_failure', 'opportunityList')

      throw err
    }
  },

  async getOpportunityDetails({ commit }, { quotationId, isPublic }) {
    commit('request_start', 'details')

    try {
      const { data } = await getOpportunityDetails(quotationId)

      const opportunity = isPublic
        ? parsePublicOpportunityDetail(data)
        : parseOpportunityDetail(data)

      commit('set_selected', { opportunity })

      commit('request_success', 'details')

      return data
    } catch (err) {
      commit('request_failure', 'details')

      throw err
    }
  },

  async buyOpportunity({ commit, state }, quotationId) {
    const { opportunityList } = state

    commit('request_start', 'purchase')

    try {
      const { data } = await buyOpportunity(quotationId)
      const newList = opportunityList.filter(opportunity => opportunity.id !== quotationId)

      commit('set_opportunity_list', newList)
      commit('request_success', 'purchase')

      return data
    } catch (err) {
      commit('request_failure', 'purchase')

      throw err
    }
  },

  async discardOpportunity({ commit, dispatch, state }, negotiationId) {
    const { opportunityList } = state
    const { opportunity } = state.selected

    commit('request_start', 'discard')

    try {
      const result = await discardOpportunity(negotiationId)

      commit('set_opportunity_list', opportunityList.filter(item => item.id !== opportunity.id))
      dispatch('getOpportunityList')

      commit('request_success', 'discard')

      return result
    } catch (err) {
      commit('request_failure', 'discard')

      throw err
    }
  },

  loadMore({ commit, dispatch, state }) {
    const { pagination } = state

    const current = pagination.current + 1
    commit('set_pagination', { current })

    dispatch('getOpportunityList')
  },

  updateSelected({ commit }, payload) {
    commit('set_selected', payload)
  },

}

export default {
  namespaced: true,

  state,
  mutations,
  actions,
}